import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Get or create session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('session_id', sessionId);
  }
  return sessionId;
};

// Logger utility
class Logger {
  constructor() {
    this.sessionId = getSessionId();
    this.queue = [];
    this.isProcessing = false;
  }

  async log(level, category, message, metadata = {}) {
    const logEntry = {
      level,
      category,
      message,
      session_id: this.sessionId,
      metadata,
      page_url: window.location.href,
      timestamp: new Date().toISOString()
    };

    // Add to queue
    this.queue.push(logEntry);

    // Process queue
    if (!this.isProcessing) {
      this.processQueue();
    }
  }

  async processQueue() {
    if (this.queue.length === 0) {
      this.isProcessing = false;
      return;
    }

    this.isProcessing = true;
    const logEntry = this.queue.shift();

    try {
      await axios.post(`${API}/logs`, logEntry);
    } catch (error) {
      console.error('Failed to send log:', error);
      // Re-queue if failed
      this.queue.unshift(logEntry);
    }

    // Process next in queue
    setTimeout(() => this.processQueue(), 100);
  }

  // Convenience methods
  info(category, message, metadata) {
    return this.log('info', category, message, metadata);
  }

  warning(category, message, metadata) {
    return this.log('warning', category, message, metadata);
  }

  error(category, message, metadata) {
    return this.log('error', category, message, metadata);
  }

  // Track page view
  pageView(pageName) {
    return this.info('page_view', `Viewed ${pageName}`, { page: pageName });
  }

  // Track user action
  userAction(action, details = {}) {
    return this.info('user_action', `User action: ${action}`, details);
  }

  // Track API call
  apiCall(endpoint, method, status, duration) {
    return this.info('api_call', `API call: ${method} ${endpoint}`, {
      endpoint,
      method,
      status,
      duration
    });
  }

  // Track performance
  performance(metric, value, details = {}) {
    return this.info('performance', `Performance: ${metric}`, {
      metric,
      value,
      ...details
    });
  }
}

const logger = new Logger();

// Track page load performance
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    const perfData = performance.timing;
    const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
    logger.performance('page_load_time', pageLoadTime, {
      page: window.location.pathname
    });
  });

  // Track JavaScript errors
  window.addEventListener('error', (event) => {
    // Filter out generic "Script error" from cross-origin scripts (like analytics, maps iframes)
    // These are security-masked errors and don't provide useful information
    if (event.message === 'Script error.' || event.message === 'Script error') {
      if (!event.filename || event.filename === '' || event.filename.includes('googletagmanager') || event.filename.includes('google-analytics') || event.filename.includes('maps.google')) {
        event.preventDefault();
        return;
      }
    }
    
    // Skip errors from external scripts (analytics, tracking, maps, etc.)
    if (event.filename && (
      event.filename.includes('googletagmanager.com') ||
      event.filename.includes('google-analytics.com') ||
      event.filename.includes('facebook.') ||
      event.filename.includes('doubleclick.net') ||
      event.filename.includes('analytics') ||
      event.filename.includes('maps.googleapis.com') ||
      event.filename.includes('maps.google.com')
    )) {
      event.preventDefault();
      return;
    }
    
    logger.error('error', `JavaScript error: ${event.message}`, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack
    });
  });

  // Track unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    logger.error('error', `Unhandled promise rejection: ${event.reason}`, {
      reason: event.reason
    });
  });
}

export default logger;